import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./NotFound.css";

/**
 * NotFound — the one 404 page for the whole site.
 *
 * Rendered from two places and nowhere else:
 *   - PublicBioPage, when the backend CONFIRMS the slug does not exist
 *     (HTTP 404). A network error, a timeout or a 5xx must never land
 *     here — those mean "we could not ask", not "there is nothing here",
 *     and telling a creator their live bio page does not exist because
 *     their wifi dropped is the worse of the two wrong answers.
 *   - App.js's `path="*"` catch-all, for anything the bio route's single
 *     segment cannot match (/abc/xyz). That route used to
 *     `<Navigate to="/">`, so a mistyped nested URL silently became the
 *     marketing homepage.
 *
 * Ported from the Claude Design export, "Variant B: Seen, no reply": a
 * sent DM gets its tick turned blue, a reply starts typing, stops, and
 * disappears. The leaf drifts down through the "0".
 *
 * TODO (deferred by product, Oct 2026) — on an unknown path UNDER A
 * RESERVED PREFIX (/analytics/xyz, /settings/xyz) this renders INSIDE
 * the app chrome, because App.js's `isReservedPath()` matches on prefix
 * and AppLayout therefore mounts the desktop sidebar and the mobile
 * bottom nav. The result shows two logos and squeezes this full-bleed
 * composition into the content column. Unknown paths OUTSIDE a reserved
 * prefix (/klgabdfja, /abc/xyz) render bare and look correct.
 *
 * Fixing it means changing AppLayout or RESERVED_PATHS, both of which
 * were deliberately left alone when this page shipped. Not a bug in this
 * component — nothing here needs to change when it is addressed.
 */

/**
 * THE LEAF.
 *
 * ⚠️ PLACEHOLDER — swap this for the leaf path out of the real Tadlo
 * logo SVG. It is the only thing standing between this page and the
 * brand mark, it is deliberately the single constant at the top of this
 * file, and nothing else needs to change when it is replaced: keep the
 * 0 0 40 56 viewBox (or update NF_LEAF_VIEWBOX to match) and the
 * animation, sizing and sway all still apply.
 */
const NF_LEAF_PATH =
  "M20 2C33 13 39 28 31.5 42.5 27.5 50 23 54 20 54s-7.5-4-11.5-11.5C1 28 7 13 20 2Z";
const NF_LEAF_VEIN = "M20 12v42";
const NF_LEAF_VIEWBOX = "0 0 40 56";

/** The app shell ships Outfit/Manrope/Inter; this page needs neither. */
const NF_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,700;12..96,800&family=Instrument+Sans:wght@400;500;600;700&display=swap";

const NF_TITLE = "Page not found | Tadlo AI";

export default function NotFound() {
  const stageRef = useRef(null);

  // ── <head/>: title + noindex ────────────────────────────────────────
  //
  // Done locally rather than through components/marketing/PageSEO.jsx on
  // purpose. PageSEO has no `robots` prop, and it also emits a canonical,
  // og:*, twitter:* and a WebPage JSON-LD node — every one of which is
  // wrong on a 404, which should assert nothing about itself beyond "do
  // not index me".
  //
  // Cleanup restores what was here before and removes ONLY what this
  // component added. A robots meta that already existed belongs to
  // whatever put it there.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = NF_TITLE;

    let addedMeta = null;
    if (!document.head.querySelector('meta[name="robots"]')) {
      addedMeta = document.createElement("meta");
      addedMeta.setAttribute("name", "robots");
      addedMeta.setAttribute("content", "noindex");
      document.head.appendChild(addedMeta);
    }

    return () => {
      document.title = previousTitle;
      if (addedMeta && addedMeta.parentNode) {
        addedMeta.parentNode.removeChild(addedMeta);
      }
    };
  }, []);

  // ── Webfonts ────────────────────────────────────────────────────────
  //
  // Bricolage Grotesque and Instrument Sans are not in public/index.html
  // (the shell ships Outfit / Manrope / Inter / JetBrains Mono), and that
  // file is not ours to edit. Injected here so the cost is paid on this
  // route alone. Every font-family in NotFound.css ends in a system
  // stack, so the copy is readable before this resolves.
  useEffect(() => {
    const existing = document.head.querySelector(
      `link[rel="stylesheet"][href="${NF_FONT_HREF}"]`
    );
    if (existing) return undefined;          // someone else owns it

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = NF_FONT_HREF;
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
    };
  }, []);

  // ── Mouse parallax ──────────────────────────────────────────────────
  //
  // Desktop pointers only. Off for touch (no pointer to follow) and off
  // for prefers-reduced-motion. The rAF handle is cancelled on unmount
  // along with both listeners, so navigating away mid-frame cannot write
  // to a detached node.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!fine.matches || reduce.matches) return undefined;

    let x = 0;
    let y = 0;
    let raf = 0;

    const apply = () => {
      stage.style.setProperty("--nf-mx", x.toFixed(3));
      stage.style.setProperty("--nf-my", y.toFixed(3));
      raf = 0;
    };
    const onMove = (e) => {
      x = (e.clientX / window.innerWidth - 0.5) * 2;
      y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (!raf) raf = window.requestAnimationFrame(apply);
    };
    const onLeave = () => {
      x = 0;
      y = 0;
      if (!raf) raf = window.requestAnimationFrame(apply);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="nf-page">
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true" focusable="false">
        <defs>
          <symbol id="nf-leaf" viewBox={NF_LEAF_VIEWBOX}>
            <path d={NF_LEAF_PATH} fill="#0C83FF" />
            <path d={NF_LEAF_VEIN} stroke="#F6F1E5" strokeWidth="2.2" strokeLinecap="round" opacity=".55" />
          </symbol>
          <symbol id="nf-bubble" viewBox="0 0 100 96">
            <path d="M38 8h24a30 30 0 0 1 30 30v10a30 30 0 0 1-30 30H44L24 92l3.5-17.6A30 30 0 0 1 8 48V38A30 30 0 0 1 38 8Z" />
          </symbol>
        </defs>
      </svg>

      <header className="nf-header">
        <Link className="nf-logo" to="/" aria-label="Tadlo AI homepage">
          <img src="/tadlo-logo.jpeg" alt="Tadlo AI" width="34" height="34" />
        </Link>
      </header>

      <main className="nf-main">
        <div className="nf-wrap">

          <div className="nf-stage nf-in" style={{ "--nf-i": 0 }} ref={stageRef} aria-hidden="true">
            <span className="nf-glow" />

            {/* background bubbles */}
            <div className="nf-par" style={{ "--nf-depth": "9px", left: "2%", bottom: "30%", width: "9%" }}>
              <span className="nf-float" style={{ "--nf-dur": "11s", "--nf-delay": "-2s", "--nf-rest": "-70px" }}>
                <svg viewBox="0 0 100 96"><use href="#nf-bubble" fill="#0C83FF" fillOpacity=".14" /></svg>
              </span>
            </div>
            <div className="nf-par" style={{ "--nf-depth": "5px", right: "3%", bottom: "8%", width: "7%" }}>
              <span className="nf-float" style={{ "--nf-dur": "9s", "--nf-delay": "-6s", "--nf-rest": "-110px" }}>
                <svg viewBox="0 0 100 96" style={{ transform: "scaleX(-1)" }}><use href="#nf-bubble" fill="none" stroke="#0C83FF" strokeWidth="7" strokeOpacity=".45" /></svg>
              </span>
            </div>
            <div className="nf-par" style={{ "--nf-depth": "4px", left: "30%", bottom: 0, width: "5%" }}>
              <span className="nf-float" style={{ "--nf-dur": "12s", "--nf-delay": "-8s", "--nf-rest": "-190px" }}>
                <svg viewBox="0 0 100 96"><use href="#nf-bubble" fill="#0A1628" fillOpacity=".1" /></svg>
              </span>
            </div>

            {/* the thread: sent + seen, then a reply that never lands */}
            <div className="nf-par" style={{ "--nf-depth": "7px", right: "12%", top: 0 }}>
              <div className="nf-sent">
                <div className="nf-sent-bubble">
                  <span className="nf-bar" />
                  <span className="nf-bar nf-bar--short" />
                </div>
                <svg className="nf-ticks" viewBox="0 0 22 12"><path d="M1.5 6.5 5 10l7-8.5" /><path d="M9 10l7-8.5M8 8.6 9 10" /></svg>
              </div>
            </div>
            <div className="nf-par" style={{ "--nf-depth": "10px", left: "6%", top: "19%" }}>
              <span className="nf-typing">
                <span className="nf-dot" style={{ "--nf-dot": "0s" }} />
                <span className="nf-dot" style={{ "--nf-dot": ".15s" }} />
                <span className="nf-dot" style={{ "--nf-dot": ".3s" }} />
              </span>
            </div>

            {/* 404 with the chat-bubble zero */}
            <div className="nf-code">
              <span className="nf-digit">4</span>
              <span className="nf-zero">
                <svg viewBox="0 0 100 96"><use href="#nf-bubble" fill="none" stroke="#0C83FF" strokeWidth="9" strokeLinejoin="round" /></svg>
                <span className="nf-leaf-fall"><span className="nf-leaf-sway"><svg viewBox={NF_LEAF_VIEWBOX}><use href="#nf-leaf" /></svg></span></span>
              </span>
              <span className="nf-digit">4</span>
            </div>
          </div>

          <p className="nf-sr-only">Error 404</p>
          <h1 className="nf-title nf-in" style={{ "--nf-i": 1 }}>This page left you on read.</h1>
          <p className="nf-sub nf-in" style={{ "--nf-i": 2 }}>The link you followed doesn't exist or has moved. Let's get you somewhere useful.</p>
          <div className="nf-actions nf-in" style={{ "--nf-i": 3 }}>
            <Link className="nf-btn nf-btn--primary" to="/">Go to homepage</Link>
            <Link className="nf-btn nf-btn--outline" to="/auth">Log in to dashboard</Link>
          </div>
          <p className="nf-help nf-in" style={{ "--nf-i": 4 }}>Need help? <a href="mailto:support@tadlo.ai">support@tadlo.ai</a></p>

        </div>
      </main>
    </div>
  );
}
